import React, { useEffect } from 'react';
import { useStore } from '../../../../../stores/root';
import { DetailsList, SearchBox, Stack } from '@fluentui/react';
import PivotButton from '../../../../uiframeworks/buttons/pivot';
import { IIncomeInvoiceResourceShortProps } from '../../../../../props/finance/incomes/invoices/invoice';
import { IIncomeInvoicePartResourceShortProps } from '../../../../../props/finance/incomes/invoices/part';
import GlobalConfig from '../../../../../config';
import IncomeService from '../../../../../services/finance/incomes';
import ErrorService from '../../../../../services/general/error';
import { faXmarkCircle } from '@fortawesome/pro-light-svg-icons';
import { IPaginationResourceShort } from '../../../../../props/general';
import IncomeInvoiceService from '../../../../../services/finance/incomes/invoices/invoice';
import moment from 'moment';
import IncomeInvoicesList from '../../invoices/general/list';
import LoadingComponent from '../../../../feedbacks/loading';
import IncomeInvoicePartService from '../../../../../services/finance/incomes/invoices/part';
import IncomeInvoicePartsList from '../../invoices/parts/general/list';

// assets

// services

// props

// components

type IncomesSummaryInvoicesProps = {
}

const IncomesSummaryInvoices: React.FC<IncomesSummaryInvoicesProps> = (props: IncomesSummaryInvoicesProps) => {
    const { banner, user } = useStore();

    const [selectedStatus, setSelectedStatus] = React.useState<'all' | 'not overdue' | 'overdue'>('all');
    const [selectedType, setSelectedType] = React.useState<'main' | 'part'>('main');
    const [keyword, setKeyword] = React.useState<string>("");
    const [loaded, setLoaded] = React.useState<boolean>(false);

    const [pagination, setPagination] = React.useState<IPaginationResourceShort>();
    const [invoices, setInvoices] = React.useState<IIncomeInvoiceResourceShortProps[]>();
    const [invoiceParts, setInvoiceParts] = React.useState<IIncomeInvoicePartResourceShortProps[]>();

    useEffect(() => {
        if (selectedType === 'main') {
            _onRetrieveInvoices();
        } else {
            _onRetrieveInvoiceParts();
        }
    }, [keyword, selectedStatus, selectedType]);

    const _onRetrieveInvoices = async (pageNumber?: number) => {
        try {
            setLoaded(false);

            const qs: string[] = [];
            qs.push(`top=${GlobalConfig.defaultTop}`);
            qs.push(`status=Unpaid`);
            qs.push(`order_by=due_date`);
            qs.push(`order_by_direction=ASC`);
            if (pageNumber) { qs.push(`page=${pageNumber}`); }
            if (keyword && keyword.trim() !== "") { qs.push(`search=${keyword}`) }
            if (selectedStatus === 'overdue') {
                qs.push(`end_due_date=${moment().startOf('day').toISOString()}`)
            } else if (selectedStatus === 'not overdue') {
                qs.push(`start_due_date=${moment().startOf('day').toISOString()}`)
            }

            const results = await IncomeInvoiceService.retrieve(qs.join("&"));
            setInvoices(results.data);
            setPagination(results.pagination);
            setLoaded(true);
        } catch (e) {
            banner.add({
                key: 'retrieve_invoices_error',
                text: 'Failed to retrieve invoices list. Error: ' + ErrorService.getMessage(e),
                icon: faXmarkCircle,
                variant: 'error'
            });
        }
    }

    const _onRetrieveInvoiceParts = async (pageNumber?: number) => {
        try {
            setLoaded(false);

            const qs: string[] = [];
            qs.push(`top=${GlobalConfig.defaultTop}`);
            qs.push(`payment_status=unpaid`);
            qs.push(`order_by=due_date`);
            qs.push(`order_by_direction=ASC`);
            if (pageNumber) { qs.push(`page=${pageNumber}`); }
            if (keyword && keyword.trim() !== "") { qs.push(`search=${keyword}`) }
            if (selectedStatus === 'overdue') {
                qs.push(`end_due_date=${moment().startOf('day').toISOString()}`)
            } else if (selectedStatus === 'not overdue') {
                qs.push(`start_due_date=${moment().startOf('day').toISOString()}`)
            }

            const results = await IncomeInvoicePartService.retrieve(undefined, qs.join("&"));
            setInvoiceParts(results.data);
            setPagination(results.pagination);
            setLoaded(true);
        } catch (e) {
            banner.add({
                key: 'retrieve_invoices_error',
                text: 'Failed to retrieve invoices list. Error: ' + ErrorService.getMessage(e),
                icon: faXmarkCircle,
                variant: 'error'
            });
        }
    }

    return <Stack tokens={{ childrenGap: 20 }}>
        <SearchBox styles={{ root: { width: 250 } }} />
        <Stack horizontal tokens={{ childrenGap: 20 }} styles={{ root: { padding: '1px 0px' } }}>
            <PivotButton buttons={[
                {
                    key: "all",
                    text: "All",
                    active: selectedStatus === 'all',
                    onClick: () => {
                        setSelectedStatus('all');
                    }
                },
                {
                    key: "notOverdue",
                    text: "Not overdue",
                    active: selectedStatus === 'not overdue',
                    onClick: () => {
                        setSelectedStatus('not overdue');
                    }
                },
                {
                    key: "overdue",
                    text: "Overdue",
                    active: selectedStatus === 'overdue',
                    onClick: () => {
                        setSelectedStatus('overdue');
                    }
                }
            ]} />
            <PivotButton buttons={[
                {
                    key: "main",
                    text: "Main invoice",
                    active: selectedType === 'main',
                    onClick: () => {
                        setSelectedType('main');
                    }
                },
                {
                    key: "part",
                    text: "Invoice part",
                    active: selectedType === 'part',
                    onClick: () => {
                        setSelectedType('part');
                    }
                }
            ]} />
        </Stack>
        {!loaded ? <LoadingComponent label={"Retrieving data ..."} labelPosition={'right'} spinnerPosition={'baseline'} /> : null}
        {selectedType === 'main' && loaded ? <IncomeInvoicesList invoices={invoices} hideCommandBar={true} columns={['invoiceNumber', 'notes', 'orderDetails', 'dueDate', 'amount', 'status']} /> : null}
        {selectedType === 'part' && loaded ? <IncomeInvoicePartsList parts={invoiceParts} hideCommandBar={true} /> : null}
    </Stack>;
};

export default IncomesSummaryInvoices;
